import React from "react";
import { useTranslation } from "react-i18next";

interface FormData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  comment: string;
  agreeTerms: boolean;
  agreeMarketing: boolean;
}

interface BookingFormProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  onSubmit: (e: React.FormEvent) => void;
  isLoading?: boolean;
}

export const BookingForm: React.FC<BookingFormProps> = ({
  formData,
  setFormData,
  onSubmit,
  isLoading = false,
}) => {
  const { t } = useTranslation("common");

  return (
    <form onSubmit={onSubmit} className="p-4 sm:p-6 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          type="text"
          required
          disabled={isLoading}
          placeholder={t("booking.firstName", "Imię *")}
          value={formData.firstName}
          onChange={(e) =>
            setFormData({ ...formData, firstName: e.target.value })
          }
          className="p-3.5 bg-white dark:bg-stone-950 border border-stone-200 dark:border-stone-800 rounded-xl text-xs font-serif text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition disabled:opacity-50"
        />
        <input
          type="text"
          required
          disabled={isLoading}
          placeholder={t("booking.lastName", "Nazwisko *")}
          value={formData.lastName}
          onChange={(e) =>
            setFormData({ ...formData, lastName: e.target.value })
          }
          className="p-3.5 bg-white dark:bg-stone-950 border border-stone-200 dark:border-stone-800 rounded-xl text-xs font-serif text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition disabled:opacity-50"
        />
      </div>

      <input
        type="tel"
        required
        disabled={isLoading}
        placeholder={t("booking.phone", "Numer telefonu *")}
        value={formData.phone}
        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
        className="w-full p-3.5 bg-white dark:bg-stone-950 border border-stone-200 dark:border-stone-800 rounded-xl text-xs font-serif text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition disabled:opacity-50"
      />

      <input
        type="email"
        required
        disabled={isLoading}
        placeholder={t("booking.email", "Adres e-mail *")}
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        className="w-full p-3.5 bg-white dark:bg-stone-950 border border-stone-200 dark:border-stone-800 rounded-xl text-xs font-serif text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition disabled:opacity-50"
      />

      <textarea
        placeholder={t("booking.comment", "Komentarz / Życzenia specjalne")}
        rows={3}
        disabled={isLoading}
        value={formData.comment}
        onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
        className="w-full p-3.5 bg-white dark:bg-stone-950 border border-stone-200 dark:border-stone-800 rounded-xl text-xs font-serif text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition resize-none disabled:opacity-50"
      />

      <div className="space-y-2 pt-2 text-[11px] font-serif text-stone-600 dark:text-stone-400">
        <label className="flex items-start space-x-2.5 cursor-pointer">
          <input
            type="checkbox"
            required
            disabled={isLoading}
            checked={formData.agreeTerms}
            onChange={(e) =>
              setFormData({ ...formData, agreeTerms: e.target.checked })
            }
            className="mt-0.5 accent-amber-600 rounded disabled:opacity-50"
          />
          <span>
            {t(
              "booking.terms",
              "Zapoznałem/am się z regulaminem i wyrażam zgodę na przetwarzanie danych. *"
            )}
          </span>
        </label>

        <label className="flex items-start space-x-2.5 cursor-pointer">
          <input
            type="checkbox"
            disabled={isLoading}
            checked={formData.agreeMarketing}
            onChange={(e) =>
              setFormData({ ...formData, agreeMarketing: e.target.checked })
            }
            className="mt-0.5 accent-amber-600 rounded disabled:opacity-50"
          />
          <span>{t("booking.marketing", "Zgoda marketingowa r-ja.pl")}</span>
        </label>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-4 mt-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-serif text-xs font-semibold uppercase tracking-widest rounded-xl transition-all duration-200 shadow-lg shadow-amber-950/40 border border-amber-400/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 cursor-pointer"
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>{t("booking.submitting", "Wysyłanie...")}</span>
          </>
        ) : (
          <span>{t("booking.submit", "Zarezerwuj stolik")}</span>
        )}
      </button>
    </form>
  );
};
